import { apiCall } from '../utils/apiUtils';
import { User, Outfit } from '../types';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

export interface DiscoveryFilters {
  eventType?: string;
  style?: string;
  brand?: string;
  priceRange?: [number, number];
  size?: string;
  color?: string;
  season?: string;
}

export interface DiscoveryResponse {
  users: User[];
  outfits: Outfit[];
  hasMore: boolean;
  nextCursor?: string;
}

export interface PopularUser extends User {
  popularityScore: number;
  trendingReason?: string;
}

export interface TrendingUser extends User {
  trendingScore: number;
  trendingReason?: string;
}

export interface UserRecommendation extends User {
  recommendationScore: number;
  recommendationReason?: string;
  category: 'trending' | 'verified' | 'new_users' | 'similar_style' | 'popular' | 'active';
  connectionInfo?: {
    mutualFollows: number;
    mutualConnections?: string[];
    connectionReason?: string;
  };
  recentActivity?: {
    outfitsThisWeek: number;
    followersThisWeek: number;
    likesReceived: number;
    engagementRate: number;
  };
}

export const discoverUsers = async (
  filters: DiscoveryFilters = {},
  limit: number = 20,
  cursor?: string
): Promise<DiscoveryResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters.eventType) params.append('eventType', filters.eventType);
    if (filters.style) params.append('style', filters.style);
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.size) params.append('size', filters.size);
    if (filters.color) params.append('color', filters.color);
    if (filters.season) params.append('season', filters.season);
    if (limit) params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);

    const response = await apiCall<DiscoveryResponse>(`${API_BASE_URL}/social/discover-users?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error discovering users:', error);
    return { users: [], outfits: [], hasMore: false };
  }
};

export const discoverOutfits = async (
  filters: DiscoveryFilters = {},
  limit: number = 20,
  cursor?: string
): Promise<DiscoveryResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters.eventType) params.append('eventType', filters.eventType);
    if (filters.style) params.append('style', filters.style);
    if (filters.brand) params.append('brand', filters.brand);
    if (filters.size) params.append('size', filters.size);
    if (filters.color) params.append('color', filters.color);
    if (filters.season) params.append('season', filters.season);
    if (limit) params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);

    const response = await apiCall<DiscoveryResponse>(`${API_BASE_URL}/outfits/discover?${params.toString()}`);
    return response;
  } catch (error) {
    console.error('Error discovering outfits:', error);
    return { users: [], outfits: [], hasMore: false };
  }
};

export const getTrendingUsers = async (limit: number = 10): Promise<User[]> => {
  try {
    const response = await apiCall<{ users: User[] }>(`${API_BASE_URL}/social/trending-users?limit=${limit}`);
    return response.users || [];
  } catch (error) {
    console.error('Error fetching trending users:', error);
    return [];
  }
};

export const searchUsers = async (query: string, limit: number = 20): Promise<User[]> => {
  try {
    if (!query.trim()) return [];
    
    const response = await apiCall<{ users: User[] }>(`${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.users || [];
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

export const getRecommendedUsers = async (userId: string, limit: number = 10): Promise<User[]> => {
  try {
    const response = await apiCall<{ users: User[] }>(`${API_BASE_URL}/social/recommended-users/${userId}?limit=${limit}`);
    return response.users || [];
  } catch (error) {
    console.error('Error fetching recommended users:', error);
    return [];
  }
};

export const getUserRecommendations = async (options: {
  limit?: number;
  category?: string;
  userId?: string;
  includeMetrics?: boolean;
}): Promise<{ 
  recommendations: UserRecommendation[]; 
  total: number; 
  personalized?: boolean;
  algorithm?: string;
}> => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.category) params.append('category', options.category);
    if (options.userId) params.append('userId', options.userId);
    if (options.includeMetrics) params.append('includeMetrics', 'true');

    const response = await apiCall<{ 
      users: User[]; 
      total?: number; 
      personalized?: boolean; 
      algorithm?: string; 
    }>(`${API_BASE_URL}/social/user-recommendations?${params.toString()}`);
    
    // Transform backend response to include category information
    const recommendations: UserRecommendation[] = (response.users || []).map((user: any) => ({
      ...user,
      recommendationScore: user.recommendationScore,
      recommendationReason: user.recommendationReason,
      category: user.category || 'popular' // Backend should provide this
    }));

    return {
      recommendations,
      total: response.total || recommendations.length,
      personalized: response.personalized || false,
      algorithm: response.algorithm || 'default'
    };
  } catch (error) {
    console.error('Error fetching user recommendations:', error);
    return { recommendations: [], total: 0, personalized: false, algorithm: 'fallback' };
  }
};

export const getPopularUsers = async (options: {
  limit?: number;
  minFollowers?: number;
  includeRecentActivity?: boolean;
}): Promise<{ users: PopularUser[]; total: number }> => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.minFollowers) params.append('minFollowers', options.minFollowers.toString());
    if (options.includeRecentActivity) params.append('includeRecentActivity', 'true');

    const response = await apiCall<{ users: User[]; total?: number }>(`${API_BASE_URL}/social/popular-users?${params.toString()}`);
    
    // Transform backend response to include popularity information
    const users: PopularUser[] = (response.users || []).map((user: any) => ({
      ...user,
      popularityScore: user.popularityScore,
      trendingReason: user.trendingReason
    }));

    return {
      users,
      total: response.total || users.length
    };
  } catch (error) {
    console.error('Error fetching popular users:', error);
    return { users: [], total: 0 };
  }
};

// Default export for compatibility
const discoveryService = {
  discoverUsers,
  discoverOutfits,
  getTrendingUsers,
  searchUsers,
  getRecommendedUsers,
  getUserRecommendations,
  getPopularUsers
};

export default discoveryService;
